import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { useMicrosoftAuth } from '../../hooks/useMicrosoftAuth';
import TwoFactorVerification from './TwoFactorVerification';
import ForceSetupTwoFactor from './ForceSetupTwoFactor';

function Login() {
    const [formData, setFormData] = useState({
        login: '',  // This will be used for either username or email
        password: ''
    });
    const [error, setError] = useState('');
    // Banner shown briefly after a successful forced-2FA enrollment, prompting
    // the user to log in again with their newly enabled 2FA.
    const [notice, setNotice] = useState('');
    // Tracks the auto-clear timer for `notice` so we can cancel it on unmount
    // and avoid a setState-on-unmounted-component warning.
    const noticeTimerRef = useRef(null);
    useEffect(() => {
        return () => {
            if (noticeTimerRef.current) {
                clearTimeout(noticeTimerRef.current);
                noticeTimerRef.current = null;
            }
        };
    }, []);
    const [isLoading, setIsLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [microsoftLoading, setMicrosoftLoading] = useState(false);

    // 2FA state
    const [requires2FA, setRequires2FA] = useState(false);
    const [tempToken, setTempToken] = useState(null);
    const [otpMethod, setOtpMethod] = useState(null);
    const [secondaryMethod, setSecondaryMethod] = useState(null);
    // Device-trust policy, handed over with the challenge. The OTP screen runs
    // before the user is authenticated, so it cannot look this up itself.
    const [deviceTrust, setDeviceTrust] = useState({ enabled: false, days: null });
    const [secondaryDestinationMask, setSecondaryDestinationMask] = useState(null);

    // Forced 2FA setup state, kicks in when org policy requires 2FA and the
    // user hasn't set it up yet. setupUser holds the partial user payload from login.
    const [requires2FASetup, setRequires2FASetup] = useState(false);
    const [setupToken, setSetupToken] = useState(null);
    const [setupUser, setSetupUser] = useState(null);
    
    const { login, googleLogin, completeTwoFactor, isAuthenticated, loading, authUnavailable } = useAuth();
    const { signInWithMicrosoft } = useMicrosoftAuth();
    
    // Custom Google login using useGoogleLogin hook
    const customGoogleLogin = useGoogleLogin({
        ux_mode: 'popup',
        select_account: true,
        onSuccess: (tokenResponse) => {
            setGoogleLoading(true);
            setError('');
            
            if (googleLogin) {
                // Convert access token format to match what googleLogin expects
                const credentialResponse = {
                    credential: tokenResponse.access_token
                };
                
                googleLogin(credentialResponse)
                    .then(result => {
                        if (result.success) {
                            if (result.requires_2fa) {
                                applyTwoFactorChallenge(result);
                            } else if (result.requires_2fa_setup) {
                                setRequires2FASetup(true);
                                setSetupToken(result.setup_token);
                                setSetupUser(result.user);
                            } else {
                                navigate(from, { replace: true });
                            }
                        } else {
                            setError(result.error || 'Google login failed');
                        }
                    })
                    .catch(err => {
                        setError('An error occurred during Google login');
                        console.error('Google login error:', err);
                    })
                    .finally(() => {
                        setGoogleLoading(false);
                    });
            } else {
                setError('Google login is not configured');
                setGoogleLoading(false);
            }
        },
        onError: () => {
            setError('Google login failed. Please try again.');
            setGoogleLoading(false);
        }
    });
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Get the redirect path from location state, localStorage, or default to dashboard
    const from = location.state?.from?.pathname
      || localStorage.getItem('jpro_login_redirect')
      || '/dashboard';

    // Redirect authenticated users
    useEffect(() => {
        if (!loading && isAuthenticated) {
            localStorage.removeItem('jpro_login_redirect');
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, loading, navigate, from]);

    // Check if redirected due to expired token
    useEffect(() => {
        if (searchParams.get('expired') === 'true') {
            setError('Your session has expired. Please log in again.');
        }
    }, [searchParams]);

    // Surface a notice passed via router state, e.g. after forced 2FA setup on
    // the signup page redirects here to sign in with the newly enabled 2FA.
    useEffect(() => {
        if (location.state?.notice) {
            setNotice(location.state.notice);
        }
    }, [location.state]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.login || !formData.password) {
            setError('Please enter both username/email and password.');
            return;
        }

        try {
            setError('');
            setIsLoading(true);

            const result = await login(formData);

            if (result.success) {
                // Check if 2FA is required
                if (result.requires_2fa) {
                    applyTwoFactorChallenge(result);
                } else if (result.requires_2fa_setup) {
                    // Org policy enforces 2FA, render forced setup wizard
                    setRequires2FASetup(true);
                    setSetupToken(result.setup_token);
                    setSetupUser(result.user);
                } else {
                    // No 2FA, proceed to dashboard
                    navigate(from, { replace: true });
                }
            } else {
                setError(result.error || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
            console.error('Login error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handle2FASuccess = (data) => {
        // Funnel the whole response through the context so it can set user,
        // token, isAuthenticated, and pending_agreements atomically. Without
        // this, ProtectedRoute sees isAuthenticated=false and bounces back
        // to /login even though the access token is valid.
        completeTwoFactor(data);
        localStorage.removeItem('jpro_login_redirect');
        navigate(from, { replace: true });
    };

    // Applied identically by all three login paths (password, Google, Microsoft).
    // Written once so a field added to the challenge cannot reach two of them
    // and miss the third.
    const applyTwoFactorChallenge = (result) => {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        setOtpMethod(result.otp_method);
        setSecondaryMethod(result.secondary_method);
        setSecondaryDestinationMask(result.secondary_destination_mask);
        setDeviceTrust({
            enabled: Boolean(result.device_trust_enabled),
            days: result.device_trust_days ?? null,
        });
    };

    const handle2FABack = (reason) => {
        setRequires2FA(false);
        setTempToken(null);
        setOtpMethod(null);
        setSecondaryMethod(null);
        setSecondaryDestinationMask(null);
        setDeviceTrust({ enabled: false, days: null });
        setFormData({ login: '', password: '' });
        // When the 2FA screen sends us back because the session expired, surface
        // that on the login form. (Guard: the Back button passes a click event.)
        setError(typeof reason === 'string' ? reason : '');
    };

    const handleMicrosoftLogin = async () => {
        try {
            setMicrosoftLoading(true);
            setError('');

            const result = await signInWithMicrosoft();

            // Check if 2FA is required
            if (result.requires_2fa) {
                applyTwoFactorChallenge(result);
                return;
            }

            if (result.requires_2fa_setup) {
                setRequires2FASetup(true);
                setSetupToken(result.setup_token);
                setSetupUser(result.user);
                return;
            }

            const finalAccessToken = result.access_token || result.token;
            if (finalAccessToken) {
                // Same problem as the 2FA path, go through the context so
                // ProtectedRoute sees isAuthenticated=true.
                completeTwoFactor(result);
                navigate(from, { replace: true });
            } else {
                setError('Microsoft login failed. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'Microsoft login failed. Please try again.');
            console.error('Microsoft login error:', err);
        } finally {
            setMicrosoftLoading(false);
        }
    };

    // Show 2FA verification screen if required
    if (requires2FA) {
        return (
            <TwoFactorVerification
                tempToken={tempToken}
                otpMethod={otpMethod}
                secondaryMethod={secondaryMethod}
                secondaryDestinationMask={secondaryDestinationMask}
                deviceTrustEnabled={deviceTrust.enabled}
                deviceTrustDays={deviceTrust.days}
                onSuccess={handle2FASuccess}
                onBack={handle2FABack}
            />
        );
    }

    // Forced 2FA setup: org policy requires 2FA and this user hasn't enrolled.
    // Renders the enrollment wizard inline; after success the user re-logs in.
    if (requires2FASetup) {
        const resetSetupState = () => {
            setRequires2FASetup(false);
            setSetupToken(null);
            setSetupUser(null);
            setFormData({ login: '', password: '' });
            setError('');
        };
        return (
            <ForceSetupTwoFactor
                setupToken={setupToken}
                user={setupUser}
                onBack={resetSetupState}
                onComplete={() => {
                    resetSetupState();
                    // Banner displayed when the login form re-renders. Clears itself
                    // after 10s; timer is cancelled on unmount to prevent
                    // setState-on-unmounted-component warnings.
                    setNotice('Two-factor authentication enabled. Sign in again to continue.');
                    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
                    noticeTimerRef.current = window.setTimeout(() => {
                        setNotice('');
                        noticeTimerRef.current = null;
                    }, 10_000);
                }}
            />
        );
    }

    return (
        <div className="w-full max-w-4xl mx-auto py-10 sm:py-14">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-blue-900/10 dark:border-gray-700 dark:bg-gray-800 dark:shadow-black/40 lg:grid lg:grid-cols-5">

                {/* Brand panel: deep blue field, always dark in both themes */}
                <div className="relative overflow-hidden bg-[#0B1B3B] px-8 py-10 sm:px-10 lg:col-span-2 lg:flex lg:flex-col lg:justify-between lg:py-12">
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl"
                    />
                    <div className="relative">
                        <p className="font-unna text-xs uppercase tracking-[0.25em] text-blue-300">
                            Zeffert &amp; Associates
                        </p>
                        <h1 className="mt-3 font-dm-serif text-5xl text-white lg:text-6xl">
                            JPRO
                        </h1>
                        <div className="mt-5 h-px w-12 bg-orange-400" />
                        <p className="mt-5 max-w-xs font-rasa text-lg leading-relaxed text-blue-100/90">
                            The intelligence platform for affordable housing compliance, audits, and training.
                        </p>
                    </div>

                    {/* Signature: AIT income designation bands, 60 set in orange */}
                    <div aria-hidden="true" className="relative mt-10 hidden select-none lg:block">
                        <p className="flex items-baseline justify-between font-dm-serif text-2xl text-blue-200/20">
                            <span>20</span>
                            <span>30</span>
                            <span>40</span>
                            <span>50</span>
                            <span className="text-orange-400/60">60</span>
                            <span>70</span>
                            <span>80</span>
                        </p>
                        <p className="mt-3 font-unna text-[10px] uppercase tracking-[0.3em] text-blue-200/40">
                            Average Income designations
                        </p>
                    </div>
                </div>

                {/* Form panel */}
                <div className="px-6 py-8 sm:px-10 sm:py-10 lg:col-span-3">
                    <h2 className="font-unna text-3xl font-bold text-gray-900 dark:text-white">
                        Sign in
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Use your JPRO account or a linked provider.
                    </p>

                    {notice && (
                        <div className="mt-5 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200">
                            <span className="font-medium">✓</span>
                            <span>{notice}</span>
                        </div>
                    )}

                    {error && (
                        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                            {error}
                        </div>
                    )}

                    {/* Renewal was rate limited or the API was briefly unreachable, which
                        is not the same thing as being signed out. Say so, so nobody reads
                        a transient blip as an expired session. Styled to match the panel's
                        other notices rather than the pre-redesign banner it arrived as. */}
                    {authUnavailable && !error && (
                        <div className="mt-5 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-200">
                            We could not reach JPRO to restore your session. Your sign-in has not
                            expired. Reload the page in a moment to pick up where you left off.
                        </div>
                    )}

                    <form className="mt-6" onSubmit={handleSubmit}>
                        <div>
                            <label
                                htmlFor="login"
                                className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                            >
                                Username or Email
                            </label>
                            <input
                                type="text"
                                id="login"
                                name="login"
                                autoComplete="username"
                                value={formData.login}
                                onChange={handleChange}
                                className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                            />
                        </div>
                        <div className="mt-5">
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="password"
                                    className="block font-unna text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                                >
                                    Password
                                </label>
                                <Link
                                    to="/forgot-password"
                                    className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                                >
                                    Forgot Password?
                                </Link>
                            </div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                autoComplete="current-password"
                                value={formData.password}
                                onChange={handleChange}
                                className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900/60 dark:text-gray-100 dark:focus:border-blue-400"
                            />
                        </div>
                        <div className="mt-7 flex justify-center">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium tracking-wide text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-blue-500 dark:hover:bg-blue-400 dark:focus:ring-offset-gray-800"
                            >
                                {isLoading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-7 flex items-center gap-4">
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                        <span className="font-unna text-xs uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                            or continue with
                        </span>
                        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                    </div>

                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <button
                            onClick={customGoogleLogin}
                            disabled={googleLoading}
                            className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
                        >
                            {googleLoading ? (
                                <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700 dark:border-gray-600 dark:border-t-gray-200"></div>
                            ) : (
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                            )}
                            {googleLoading ? 'Signing in...' : 'Continue with Google'}
                        </button>

                        <button
                            onClick={handleMicrosoftLogin}
                            disabled={microsoftLoading}
                            className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
                        >
                            {microsoftLoading ? (
                                <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700 dark:border-gray-600 dark:border-t-gray-200"></div>
                            ) : (
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23">
                                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                                </svg>
                            )}
                            {microsoftLoading ? 'Signing in...' : 'Continue with Microsoft'}
                        </button>
                    </div>

                    <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        Don&apos;t have an account?{" "}
                        <Link
                            to="/signup"
                            className="font-medium text-orange-600 hover:underline dark:text-orange-300"
                        >
                            Create One
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Login;